import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import { AuthProvider, useAuth } from './hooks/useAuth';
import PrivateRoute from './components/PrivateRoute';
import AppLayout from './components/AppLayout';

// Lazy pages
const LoginPage           = lazy(() => import('./pages/LoginPage'));
const ForgotPasswordPage  = lazy(() => import('./pages/ForgotPasswordPage'));
const AgendaPage          = lazy(() => import('./pages/AgendaPage'));
const ClientesPage        = lazy(() => import('./pages/ClientesPage'));
const ClienteDetallePage  = lazy(() => import('./pages/ClienteDetallePage'));
const TiposServicioPage   = lazy(() => import('./pages/TiposServicioPage'));
const DisponibilidadPage  = lazy(() => import('./pages/DisponibilidadPage'));
const PerfilPage          = lazy(() => import('./pages/PerfilPage'));
const PerfilEditPage      = lazy(() => import('./pages/PerfilEditPage'));
const NegocioAdminPage    = lazy(() => import('./pages/NegocioAdminPage'));
const EstadisticasPage    = lazy(() => import('./pages/EstadisticasPage'));
const PublicProfilePage   = lazy(() => import('./pages/PublicProfilePage'));
const PublicNegocioPage   = lazy(() => import('./pages/PublicNegocioPage'));
const AccesoDenegadoPage  = lazy(() => import('./pages/AccesoDenegadoPage'));

const ROLES_ALL     = ['SuperAdmin', 'AdminNegocio', 'Recepcionista', 'Profesional'];
const ROLES_ADMIN   = ['SuperAdmin', 'AdminNegocio'];
const ROLES_NEGOCIO = ['SuperAdmin', 'AdminNegocio', 'Recepcionista'];
const ROLES_STATS   = ['SuperAdmin', 'AdminNegocio', 'Profesional'];

function Layout({ children, requiredRoles }) {
  return (
    <PrivateRoute requiredRoles={requiredRoles}>
      <AppLayout>{children}</AppLayout>
    </PrivateRoute>
  );
}

function PageLoader() {
  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
      <CircularProgress />
    </Box>
  );
}

function RootRedirect() {
  const { isAuthenticated, homeRoute } = useAuth();
  return <Navigate to={isAuthenticated ? homeRoute : '/login'} replace />;
}

function App() {
  return (
    <AuthProvider>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Raíz */}
          <Route path="/" element={<RootRedirect />} />

          {/* Públicas — sin auth */}
          <Route path="/login"                element={<LoginPage />} />
          <Route path="/recuperar-password"   element={<ForgotPasswordPage />} />
          <Route path="/p/:username"          element={<PublicProfilePage />} />
          <Route path="/n/:slug"              element={<PublicNegocioPage />} />
          <Route path="/acceso-denegado"      element={<AccesoDenegadoPage />} />

          {/* Agenda propia del profesional */}
          <Route path="/agenda" element={
            <Layout requiredRoles={ROLES_ALL}><AgendaPage /></Layout>
          } />

          {/* Agenda del negocio (recepcionista y admin) */}
          <Route path="/negocio/agenda" element={
            <Layout requiredRoles={ROLES_NEGOCIO}><AgendaPage negocioView /></Layout>
          } />

          {/* Clientes */}
          <Route path="/clientes" element={
            <Layout requiredRoles={ROLES_ALL}><ClientesPage /></Layout>
          } />
          <Route path="/clientes/:id" element={
            <Layout requiredRoles={ROLES_ALL}><ClienteDetallePage /></Layout>
          } />

          {/* Tipos de servicio */}
          <Route path="/servicios" element={
            <Layout requiredRoles={ROLES_ALL}><TiposServicioPage /></Layout>
          } />

          {/* Disponibilidad / horarios */}
          <Route path="/disponibilidad" element={
            <Layout requiredRoles={ROLES_ALL}><DisponibilidadPage /></Layout>
          } />

          {/* Perfil */}
          <Route path="/perfil" element={
            <Layout requiredRoles={ROLES_ALL}><PerfilPage /></Layout>
          } />
          <Route path="/perfil/editar" element={
            <Layout requiredRoles={ROLES_ALL}><PerfilEditPage /></Layout>
          } />

          {/* Administración del negocio */}
          <Route path="/negocio/admin" element={
            <Layout requiredRoles={ROLES_ADMIN}><NegocioAdminPage /></Layout>
          } />

          {/* Estadísticas */}
          <Route path="/estadisticas" element={
            <Layout requiredRoles={ROLES_STATS}><EstadisticasPage /></Layout>
          } />

          {/* Admin super */}
          <Route path="/admin/usuarios" element={
            <Layout requiredRoles={['SuperAdmin']}><AgendaPage /></Layout>
          } />

          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </AuthProvider>
  );
}

export default App;
